// Demo menu (reviewers only): persona, plan, theme, reset, KPI event log.
// Opens with ⌘. / Ctrl+. or five taps on the logo.
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Plan } from '../../domain/types';
import { LADDER, PLANS } from '../../domain/entitlements';
import { Button } from '../components/Button';
import { Segmented } from '../components/Segmented';
import { Sheet } from '../components/Sheet';
import { Icon } from '../icons';
import { clearEvents, getEvents, subscribeEvents } from '../lib/track';
import { onOpenDemoMenu } from './demoBus';
import { PERSONAS } from './personas';
import { usePlan } from './PlanProvider';
import { ThemeSwitch } from './ThemeSwitch';
import { Toggle } from '@aurora-reactkit/components/toggle';
import { setShowLab, useShowLab } from './labPrefs';
import s from './DemoMenu.module.css';

const PLAN_SHORT: Record<Plan, string> = { free_scan: 'Free', basic: 'Basic', complete: 'Complete', ultimate: 'Ultimate' };

export function DemoMenu() {
  const [open, setOpen] = useState(false);
  const { persona, plan, setPersona, setPlan, reset } = usePlan();
  const events = useSyncExternalStore(subscribeEvents, getEvents, getEvents);
  const close = useCallback(() => setOpen(false), []);
  const navigate = useNavigate();
  const showLab = useShowLab();

  useEffect(() => onOpenDemoMenu(() => setOpen(true)), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onReset = async () => { await reset(); clearEvents(); };

  return (
    <Sheet open={open} onClose={close} title="Demo menu" variant="drawer" testId="demo-menu">
      <section className={s.section} aria-labelledby="demo-persona">
        <h3 id="demo-persona" className={s.h}>Persona</h3>
        <div role="radiogroup" aria-labelledby="demo-persona" className={s.list}>
          {PERSONAS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={p.id === persona}
              className={s.row}
              data-testid={`demo-persona-${p.id}`}
              onClick={() => setPersona(p.id)}
            >
              <span className={s.rowText}>
                <span className={s.rowTitle}>{p.name}</span>
                <span className={s.rowSub}>{PLANS[p.plan].name} · {p.blurb}</span>
              </span>
              {p.id === persona && <Icon name="circle-check" size={22} className={s.check} />}
            </button>
          ))}
        </div>
      </section>

      <section className={s.section} aria-labelledby="demo-plan">
        <h3 id="demo-plan" className={s.h}>Plan</h3>
        <Segmented<Plan>
          label="Plan"
          value={plan}
          onChange={setPlan}
          options={LADDER.map((p) => ({ value: p, label: PLAN_SHORT[p], testId: `demo-plan-${p}` }))}
        />
      </section>

      <section className={s.section} aria-labelledby="demo-theme">
        <h3 id="demo-theme" className={s.h}>Theme</h3>
        <ThemeSwitch testPrefix="demo-theme" />
      </section>

      <section className={s.section} aria-labelledby="demo-events">
        <div className={s.hRow}>
          <h3 id="demo-events" className={s.h}>Event log</h3>
          <span className={s.count}>{events.length}</span>
        </div>
        <ol className={s.log} data-testid="event-log" aria-live="polite">
          {events.length === 0 && <li className={s.empty}>No events yet. Actions you take appear here.</li>}
          {[...events].reverse().map((e, i) => (
            <li key={`${e.at}-${i}`} className={s.ev}>
              <span className={s.evName}>{e.name}</span>
              <time className={s.evTime} dateTime={e.at}>{new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
              {Object.keys(e.props).length > 0 && <code className={s.evProps}>{JSON.stringify(e.props)}</code>}
            </li>
          ))}
        </ol>
      </section>

      <Button variant="secondary" block onClick={() => { close(); navigate('/welcome'); }} data-testid="demo-onboarding">
        Start onboarding
      </Button>
      <div className={s.toggleRow}>
        <label htmlFor="demo-show-lab" className={s.toggleText}>
          <span className="bodySmMedium">Show Footprint lab</span>
          <span className={s.rowSub}>The 10 design variants in the sidebar</span>
        </label>
        <Toggle id="demo-show-lab" checked={showLab} onChange={setShowLab} data-testid="demo-show-lab" />
      </div>
      <Link to="/lab" className={`bodySmMedium ${s.labLink}`} onClick={close} data-testid="demo-lab">Footprint lab: 10 design variants</Link>
      <Link to="/story" className={`bodySmMedium ${s.labLink}`} onClick={close} data-testid="demo-story">Monthly story (mobile)</Link>
      <Button variant="secondary" block onClick={onReset} data-testid="demo-reset">
        <Icon name="rotate-left" size={20} /> Reset demo data
      </Button>
      <p className={s.hint}>Shortcut: ⌘. , the flask in the header, Demo menu in the account menu, or five taps on the logo.</p>
    </Sheet>
  );
}
